/**
 * NavRail — the vertical panel switcher down the left edge.
 *
 * Every nav button is present at once. The entries, their order and what a click on one does
 * are in navEntries, which the home launcher draws from too, so the rail and the launcher offer
 * the same places. The rail's own job is the shape: the two halves, the rules between them, and
 * the groups that stay pinned however short the window is.
 *
 * Groups, top to bottom. Rules separate the groups 1 to 4:
 *   1. The logo (only when the chat drawer is closed — with the drawer open
 *      the logo sits over IT, and the rail starts at the top).
 *   2. The view entries, Cameras first.
 *   3. Camera settings, then the rest of the settings entries.
 *   4. Close all, while a panel is on the stage.
 *   5. Connectivity badges and avatar — pinned to the bottom.
 *
 * AI Chat has no button here: it lives on the logo (see AppShell). The phone has
 * no logo, rail or drawer; the same list fills its screen while no panel is open
 * (`fullScreen`, PhoneNav), every entry labelled and AI chat heading the list as
 * a panel like the others.
 *
 * The rail is drag-resizable between an icon-only and a labelled shape; the
 * width model, including why the middle of the range snaps, is in navResize.ts.
 *
 * Height is the harder constraint: the buttons are fixed-height and the shell
 * is `h-dvh`, so on a short window the bottom group used to be pushed out of
 * the viewport entirely — taking sign-out and the org switcher with it,
 * unreachable because the page does not scroll. The button
 * groups therefore live in a scroll container that gives up height first,
 * which keeps the account group pinned no matter how short the window is.
 */

import { useAtomValueRawSync } from 'jotai';
import { Sparkles } from 'lucide-react';
import type { CSSProperties, ReactElement } from 'react';
import React from 'react';

import { CloudServersBadge } from '../CloudServersBadge';
import { UserMenu } from '../UserMenu';
import { CancelIcon } from '@/components/occurra/icons/CancelIcon';
import { NavRailButton } from '@/components/occurra/ui/NavRailButton';
import { useDoUpdatesNeedAttention } from '@/hooks/useDoUpdatesNeedAttention';
import { useMemberPermissions } from '@/hooks/useMemberPermissions';
import { cn } from '@/lib/utils';
import { openIdsAtom, visibleIdsAtom } from '@/state/ui/shellPanelsAtoms';
import { navWidthAtom } from '@/state/ui/shellRecordAtoms';
import type { NavEntry, OpenPanelFn, PanelId } from '@/types/panels';
import { PanelMode } from '@/types/panels';

import { CHAT_PANEL_ID } from './ChatDrawer';
import { isNavEntryOpen, listNavEntries, openNavEntry } from './navEntries';
import {
    getRailWidth, NAV_MIN, NAV_PAD, showsLabels,
} from './navResize';

interface NavRailProps {
    openPanel: OpenPanelFn;
    /** Closes every panel on the stage. The phone has no use for it: its list shows only on an
     *  empty stage. */
    onCloseAll?: () => void;
    reserveLogoSpace: boolean;
    /** Lays the rail out as the phone's whole screen. */
    fullScreen?: boolean;
}

// Vertical room the floating logo occupies before the rail's own content:
// exactly the mark's height (size-9), since the rail's padding already offsets
// both by the same amount — the logo and the first panel share a top edge.
// The rule drawn underneath supplies the separation, so no extra gap here.
const LOGO_SPACER_PX = 36;

/** The icon-only button width, as the custom property that navRailButton.css squares it with. */
interface RailStyle extends CSSProperties {
    '--occ-nav-min': string;
}

const RAIL_STYLE: RailStyle = { '--occ-nav-min': `${NAV_MIN}px` };

// The line inside a rule between groups of buttons. Inset 4px on each side, so it reads as part
// of the button column, not of the full rail (Figma 1744:19993).
const RULE_LINE = 'h-px w-full bg-occ-foreground-50';
const RULE = (
    <div
        className="my-1 flex h-4 shrink-0 items-center px-1"
        aria-hidden="true"
    >
        <div className={RULE_LINE} />
    </div>
);

export const NavRail = ({
    openPanel,
    onCloseAll,
    reserveLogoSpace: shouldReserveLogoSpace,
    fullScreen: isFullScreen = false,
}: NavRailProps): ReactElement => {
    const navWidth = useAtomValueRawSync(navWidthAtom);
    const visibleIds = useAtomValueRawSync(visibleIdsAtom);
    const openIds = useAtomValueRawSync(openIdsAtom);
    const isLabelled = isFullScreen || showsLabels(navWidth);
    const { canManage } = useMemberPermissions();
    const doUpdatesNeedAttention = useDoUpdatesNeedAttention();

    // Nothing is filtered OUT by mode — that is the point of v3 — but a panel that only
    // exists in settings sorts below the rule. The list, the order and what a click does are
    // the launcher's too (navEntries).
    const entries = listNavEntries(canManage);
    const openState = { visibleIds, openIds };
    const groups = [PanelMode.View, PanelMode.Settings]
        .map((group) => entries.filter((entry) => entry.group === group))
        .filter((group) => group.length > 0); //< A rule only ever separates two real groups.
    const handleClick = (id: PanelId): void => openNavEntry(id, openState, openPanel);

    return (
        <nav
            aria-label="Panels"
            // No right padding: the stage's --pad already separates the rail from
            // the first panel, so the buttons sit as far from the panel as from the
            // window edge. See navResize.ts. Full screen, it pads all four sides.
            style={isFullScreen
                ? { ...RAIL_STYLE, padding: NAV_PAD }
                : {
                    ...RAIL_STYLE,
                    width: getRailWidth(navWidth),
                    paddingLeft: NAV_PAD,
                    paddingTop: NAV_PAD,
                    paddingBottom: NAV_PAD,
                }}
            className={cn(
                'relative flex flex-col gap-1',
                isFullScreen ? 'min-h-0 flex-1' : 'shrink-0',
            )}
        >
            {/* With the chat drawer closed the logo shares this column with the
          buttons, so it gets a rule of its own — it is a control, not the
          first nav entry. With the drawer open the logo sits over IT instead,
          in its own column, and needs no separating.

          Always mounted, collapsed by height, so the buttons SLIDE between the
          two arrangements over the drawer's own 200ms instead of jumping the
          moment it opens. grid-template-rows 0fr⇄1fr rather than a hard-coded
          height: the block is a 36px spacer plus a rule, and a number copied
          out of their classes would rot the first time their spacing changes.

          -mb-1 cancels the flex gap that follows this child, and pb-1 inside
          puts it back when expanded — so collapsed really is zero, not one
          leftover gap, and expanded measures exactly what the two conditional
          children used to. Full screen, the logo heads the screen instead. */}
            {!isFullScreen && (
                <div
                    data-reserved={shouldReserveLogoSpace || undefined}
                    className={cn(
                        'grid -mb-1 shrink-0 [grid-template-rows:0fr]',
                        'data-[reserved]:[grid-template-rows:1fr]',
                        'transition-[grid-template-rows] duration-200',
                        'ease-[cubic-bezier(0.4,0,0.2,1)]',
                        'motion-reduce:transition-none',
                    )}
                >
                    <div className="overflow-hidden">
                        <div className="flex flex-col gap-1 pb-1">
                            <div
                                style={{ height: LOGO_SPACER_PX }}
                                className="shrink-0"
                            />
                            {/* Icon-only, the logo and the icons are one column; a line
                                between them only cuts it, so the line hides and keeps its
                                room. 8px of room, so the icons sit close under the logo. */}
                            <div
                                className="flex h-2 shrink-0 items-center px-1"
                                aria-hidden="true"
                            >
                                <div className={cn(RULE_LINE, !isLabelled && 'invisible')} />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Takes the leftover height when there is any (so the group below is
          pinned to the bottom) and scrolls when there is not. `min-h-0` is
          what lets a flex child shrink past its content. The scrollbar is
          hidden: at a 36px rail it would eat a third of the width, and the
          same treatment is already used for the phone panel cluster. */}
            <div className={cn(
                // No gap: the buttons sit flush, their own 36/32px height supplying all
                // the air the column needs. The rules between groups keep their my-1,
                // so the groups still read as separated.
                'flex min-h-0 flex-1 flex-col overflow-y-auto',
                '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            )}
            >
                {/* Without the logo the chat is a destination like any other, and
              the one the list leads with. */}
                {isFullScreen && (
                    <>
                        <NavRailButton
                            icon={Sparkles}
                            label="AI chat"
                            isExpanded
                            isRoomy
                            isPressed={visibleIds.has(CHAT_PANEL_ID)}
                            onClick={(): void => handleClick(CHAT_PANEL_ID)}
                        />
                        {RULE}
                    </>
                )}
                {groups.map((group, i) => (
                    <React.Fragment key={group[0]?.id ?? i}>
                        {i > 0 && RULE}
                        {group.map((entry: NavEntry) => (
                            <NavRailButton
                                key={entry.id}
                                icon={entry.icon}
                                label={entry.label}
                                isExpanded={isLabelled}
                                isRoomy={isFullScreen}
                                isPressed={isNavEntryOpen(entry.id, openState)}
                                hasDot={entry.id === 'servers' && doUpdatesNeedAttention}
                                onClick={(): void => handleClick(entry.id)}
                            />
                        ))}
                    </React.Fragment>
                ))}
                {onCloseAll && openIds.size > 0 && (
                    <>
                        {RULE}
                        <NavRailButton
                            icon={CancelIcon}
                            label="Close all"
                            isExpanded={isLabelled}
                            onClick={onCloseAll}
                        />
                    </>
                )}
            </div>

            {/* Bottom group: connectivity and account, pinned below the destination list. */}
            <div className="flex shrink-0 flex-col">
                {/* Collapses when no badge shows, so an all-clear rail keeps its shape. */}
                <div className="flex flex-col gap-1 pb-1 empty:hidden">
                    <CloudServersBadge
                        isLabelled={isLabelled}
                        isRoomy={isFullScreen}
                    />
                </div>
                <UserMenu
                    shouldShowTheme={true}
                    shouldShowName={isLabelled}
                    side={isFullScreen ? 'top' : 'right'}
                    align={isFullScreen ? 'start' : 'end'}
                />
            </div>
        </nav>
    );
};
